import {indexBy, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {ChangeOrderRentVehicleRequestBody} from '@dms/api/rental';
import {VatRateOption} from '@dms/shared';

import {Nullish} from 'shared/web';

import {RentVehicleFormValues} from '../types/RentVehicleFormValues';

export const getRentVehicleRequestBody = (
  data: RentVehicleFormValues,
  vatTypes: VatRateOption[]
): ChangeOrderRentVehicleRequestBody => {
  const vatRatesByValue = indexBy((item) => item.value, vatTypes);

  const getRateByType = (type: string | Nullish) => {
    if (isNil(type)) {
      return '0';
    }

    return vatRatesByValue?.[type ?? '']?.rate ?? vatRatesByValue?.S?.rate ?? '0';
  };

  const customRentPrice =
    data.isCustomPriceEnabled &&
    data.customPrice?.withoutVat &&
    data.customPrice?.withVat &&
    data.customPrice?.vat &&
    data.customPrice?.vatType
      ? {
          withoutVat: data.customPrice.withoutVat.toString(),
          vat: data.customPrice.vat.toString(),
          withVat: data.customPrice.withVat.toString(),
          vatRate: getRateByType(data.customPrice.vatType),
          vatType: data.customPrice.vatType,
        }
      : null;

  return {
    actualMileage: data.actualMileage,
    authorizedPersons: data.authorizedPersons.map((item) => ({
      ...item,
      idCardNumber: isNilOrEmpty(item.idCardNumber) ? null : item.idCardNumber,
      drivingLicenceNumber: isNilOrEmpty(item.drivingLicenceNumber)
        ? null
        : item.drivingLicenceNumber,
      phoneNumber:
        isNil(item.phoneNumber) || isNilOrEmpty(item.phoneNumber?.number) ? null : item.phoneNumber,
    })),
    customRentPrice,
    equipment: data.equipment,
    fuelTank: data.fuelTank.toString(),
    rentAddress: data.rentAddress,
    returnAddress: data.isAddressSame ? null : data.returnAddress || null,
    rentPrice: {
      dailyRate: data.rentPrice.dailyRate.toString(),
      paidDays: data.rentPrice.paidDays,
      freeDailyKilometers: data.rentPrice.freeDailyKilometers,
      totalFreeKilometers: data.rentPrice.totalFreeKilometers,
      rentalPrice: {
        withoutVat: data.rentPrice.rentalPrice.withoutVat.toString(),
        vat: data.rentPrice.rentalPrice.vat.toString(),
        withVat: data.rentPrice.rentalPrice.withVat.toString(),
        vatRate: getRateByType(data.rentPrice.rentalPrice.vatType),
        vatType: data.rentPrice.rentalPrice.vatType,
      },
      pricePerKilometer: {
        withoutVat: data.rentPrice.pricePerKilometer.withoutVat.toString(),
        vat: data.rentPrice.pricePerKilometer.vat.toString(),
        withVat: data.rentPrice.pricePerKilometer.withVat.toString(),
        vatRate: getRateByType(data.rentPrice.pricePerKilometer.vatType),
        vatType: data.rentPrice.pricePerKilometer.vatType,
      },
      deliveryPrice: {
        withoutVat: data.rentPrice.deliveryPrice?.withoutVat?.toString() || '0',
        vat: data.rentPrice.deliveryPrice?.vat?.toString() || '0',
        withVat: data.rentPrice.deliveryPrice?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.deliveryPrice?.vatType),
        vatType: data.rentPrice.deliveryPrice?.vatType ?? 'S',
      },
      otherPrice: {
        withoutVat: data.rentPrice.otherPrice?.withoutVat?.toString() || '0',
        vat: data.rentPrice.otherPrice?.vat?.toString() || '0',
        withVat: data.rentPrice.otherPrice?.withVat?.toString() || '0',
        vatRate: getRateByType(data.rentPrice.otherPrice?.vatType),
        vatType: data.rentPrice.otherPrice?.vatType ?? 'S',
      },
      discount: {
        percent: data.rentPrice.discount?.percent?.toString() || '0',
        discount: {
          withoutVat: data.rentPrice.discount?.discount?.withoutVat?.toString() || '0',
          vat: data.rentPrice.discount?.discount?.vat?.toString() || '0',
          withVat: data.rentPrice.discount?.discount?.withVat?.toString() || '0',
          vatRate: getRateByType(data.rentPrice.discount?.discount?.vatType),
          vatType: data.rentPrice.discount?.discount?.vatType ?? 'S',
        },
      },
    },
    rentType: data.rentType,
  };
};
